// Detalhe de um certificado do cofre: o que ele é, o que depende dele, o que
// já aconteceu com ele — e as ações que valem para todas as configurações
// vinculadas de uma vez.
import {
  ArrowLeftIcon,
  PencilIcon,
  RefreshCwIcon,
  UploadIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom"

import { CertificateStatusBadge } from "@/components/commons/certificate-status-badge"
import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { Header } from "@/components/layout/header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { FRONTEND_ROUTES } from "@/constants"
import { MtlsCertificateHistory } from "@/features/merchant-admin/components/mtls-certificate-history"
import { MtlsCertificateLinkedConfigs } from "@/features/merchant-admin/components/mtls-certificate-linked-configs"
import { RenameMtlsCertificateDialog } from "@/features/merchant-admin/components/rename-mtls-certificate-dialog"
import { ReplaceMtlsCertificateDialog } from "@/features/merchant-admin/components/replace-mtls-certificate-dialog"
import { useUrlTab } from "@/hooks/use-url-tab"
import {
  useMerchantMtlsCertificate,
  useRenewMerchantMtlsCertificateMutation,
} from "@/services/admin/merchant-mtls-certificates"

const panelPrefix = "pages.adminMerchants"

const DEFAULT_TAB = "linkedConfigs"
const TABS = [DEFAULT_TAB, "history"] as const

const Field = ({
  label,
  value,
}: {
  label: string
  value?: string | null
}) => (
  <div className="space-y-1">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="break-all font-mono text-xs">
      {value || "—"}
    </p>
  </div>
)

export function MerchantMtlsCertificateDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { certificateId = "" } = useParams()
  const { data: certificate, isLoading } =
    useMerchantMtlsCertificate(certificateId)
  const renewMutation =
    useRenewMerchantMtlsCertificateMutation()
  const [isReplaceOpen, setIsReplaceOpen] = useState(false)
  const [isRenameOpen, setIsRenameOpen] = useState(false)
  const [activeTab, handleTabChange] = useUrlTab(
    TABS,
    DEFAULT_TAB
  )

  // A aba do cofre fica na página do lojista; voltar tem que cair nela, não na
  // primeira aba da ficha.
  const backHref = `${FRONTEND_ROUTES.ADMIN_MERCHANT.href}?tab=mtlsCertificates`

  return (
    <main className="flex w-full flex-1 flex-col">
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            {
              href: backHref,
              label: t(
                `${panelPrefix}.tabs.mtlsCertificates`
              ),
            },
            { label: certificate?.name ?? certificateId },
          ]}
        />
      </Header>

      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <Button
          variant="ghost"
          size="sm"
          className="w-fit"
          asChild
        >
          <Link to={backHref}>
            <ArrowLeftIcon
              className="mr-1.5 size-4"
              aria-hidden="true"
            />
            {t(`${panelPrefix}.mtlsCertificates.back`)}
          </Link>
        </Button>

        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : !certificate ? (
          <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {t(`${panelPrefix}.mtlsCertificates.notFound`)}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="page-title">
                  {certificate.name}
                </h1>
                <CertificateStatusBadge
                  status={certificate.certificate.status}
                  daysToExpire={
                    certificate.certificate.daysToExpire
                  }
                />
                {certificate.gateways.map((gateway) => (
                  <Badge
                    key={gateway}
                    variant="outline"
                    className="border-violet-200 bg-violet-50 text-violet-700"
                  >
                    {gateway}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {/* O alcance: quantas configurações param se este vencer. */}
                {t(
                  `${panelPrefix}.mtlsCertificates.linkedConfigs`,
                  { count: certificate.linkedConfigsCount }
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-xl border p-4 sm:grid-cols-2">
              <Field
                label={t(
                  "pages.adminProcessorConfig.detail.certificateSubject"
                )}
                value={certificate.certificate.subject}
              />
              <Field
                label={t(
                  "pages.adminProcessorConfig.detail.certificateIssuer"
                )}
                value={certificate.certificate.issuer}
              />
              <Field
                label={t(
                  "pages.adminProcessorConfig.detail.certificateValidity"
                )}
                value={
                  certificate.certificate.notBefore ||
                  certificate.certificate.notAfter
                    ? `${certificate.certificate.notBefore ?? "—"} → ${certificate.certificate.notAfter ?? "—"}`
                    : null
                }
              />
              <Field
                label="SHA-256"
                value={
                  certificate.certificate.fingerprintSha256
                }
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsRenameOpen(true)}
              >
                <PencilIcon
                  className="mr-1.5 size-4"
                  aria-hidden="true"
                />
                {t(
                  `${panelPrefix}.mtlsCertificates.renameCta`
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsReplaceOpen(true)}
              >
                <UploadIcon
                  className="mr-1.5 size-4"
                  aria-hidden="true"
                />
                {t(
                  `${panelPrefix}.mtlsCertificates.replaceCta`
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={renewMutation.isPending}
                onClick={() =>
                  renewMutation.mutate(certificate.id)
                }
              >
                <RefreshCwIcon
                  className={
                    renewMutation.isPending
                      ? "mr-1.5 size-4 animate-spin"
                      : "mr-1.5 size-4"
                  }
                  aria-hidden="true"
                />
                {t(`${panelPrefix}.mtlsCertificates.renew`)}
              </Button>
            </div>

            {/* Aba na URL: quem chega por link cai direto no que veio ver, e voltar
                do painel de processadores nao perde o contexto. */}
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
            >
              <TabsList>
                <TabsTrigger value="linkedConfigs">
                  {t(
                    `${panelPrefix}.mtlsCertificates.linkedConfigsTitle`
                  )}
                </TabsTrigger>
                <TabsTrigger value="history">
                  {t(
                    `${panelPrefix}.mtlsCertificates.historyTitle`
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="linkedConfigs"
                className="pt-4"
              >
                <MtlsCertificateLinkedConfigs
                  configs={certificate.linkedConfigs ?? []}
                  orphanProviders={
                    certificate.orphanProviders ?? 0
                  }
                />
              </TabsContent>

              <TabsContent value="history" className="pt-4">
                <MtlsCertificateHistory
                  certificateId={certificate.id}
                />
              </TabsContent>
            </Tabs>

            <RenameMtlsCertificateDialog
              certificateId={certificate.id}
              certificateName={certificate.name}
              open={isRenameOpen}
              onOpenChange={setIsRenameOpen}
            />

            <ReplaceMtlsCertificateDialog
              certificateId={certificate.id}
              certificateName={certificate.name}
              linkedConfigsCount={
                certificate.linkedConfigsCount
              }
              open={isReplaceOpen}
              onOpenChange={(open) => {
                setIsReplaceOpen(open)
                if (!open) navigate(0)
              }}
            />
          </>
        )}
      </section>
    </main>
  )
}
